import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { collection, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection } from '@/hooks/firestore';
import { useAuth } from '@/stores/auth';
import { EmptyState, LoadingSkeleton } from '@/components/ui/Card';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { StatusChip } from '@/components/ui/StatusChip';
import { formatBookingWhen } from '@/lib/formatRelative';
import {
  BOOKING_STATUS_LABEL,
  BOOKING_STATUS_TONE,
  bookingNextAction,
} from '@/lib/bookingLabels';
import { formatZAR, type BookingDoc } from '@makhe/shared';

type Tab = 'upcoming' | 'past' | 'cancelled';

const UPCOMING = [
  'requested',
  'counter_proposed',
  'accepted',
  'in_progress',
  'awaiting_confirmation',
  'disputed',
];

const PAST = ['completed'];
const CANCELLED = [
  'declined',
  'cancelled_by_buyer',
  'cancelled_by_seller',
  'no_show_buyer',
  'no_show_seller',
];

export default function BookingsPage() {
  const { firebaseUser, isSeller } = useAuth();
  const [side, setSide] = useState<'buyer' | 'seller'>('buyer');
  const [tab, setTab] = useState<Tab>('upcoming');

  const q = useMemo(() => {
    if (!firebaseUser) return null;
    // Order by scheduledStart (the diary field) — there are composite indexes for
    // buyerId+scheduledStart and sellerId+scheduledStart. Ordering by createdAt
    // here had no matching index, so the query errored (and, before the error
    // state was surfaced, silently showed an empty list).
    return query(
      collection(db, 'bookings'),
      where(side === 'buyer' ? 'buyerId' : 'sellerId', '==', firebaseUser.uid),
      orderBy('scheduledStart', 'desc'),
      limit(50),
    );
  }, [firebaseUser, side]);
  const { data: bookings, loading, error } = useCollection<BookingDoc>(q);

  const visible = useMemo(() => {
    const set = tab === 'upcoming' ? UPCOMING : tab === 'past' ? PAST : CANCELLED;
    return bookings.filter((b) => set.includes(b.status));
  }, [bookings, tab]);

  return (
    <section className="mx-auto max-w-3xl px-5 py-6 pb-10 md:px-6">
      <div className="flex items-end justify-between gap-4 border-b border-makhe-line pb-4">
        <div>
          <p className="eyebrow mb-2">Your diary</p>
          <h1
            className="font-display text-3xl leading-none md:text-4xl"
            style={{ fontVariationSettings: '"opsz" 72, "wght" 450' }}
          >
            Bookings
          </h1>
        </div>
        {isSeller() && (
          <SegmentedControl
            size="sm"
            value={side}
            onChange={setSide}
            options={[
              { value: 'buyer', label: 'Buying' },
              { value: 'seller', label: 'Selling' },
            ]}
          />
        )}
      </div>

      <div className="mt-5">
        <SegmentedControl
          value={tab}
          onChange={setTab}
          options={[
            { value: 'upcoming', label: 'Upcoming' },
            { value: 'past', label: 'Past' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
        />
      </div>

      <div className="mt-6 space-y-3">
        {loading && (
          <>
            <LoadingSkeleton className="h-28" />
            <LoadingSkeleton className="h-28" />
          </>
        )}
        {!loading && error && (
          <EmptyState
            title="Couldn’t load your bookings"
            description="Something went wrong. Check your connection and try again."
          />
        )}
        {!loading && !error && visible.length === 0 && (
          <EmptyState
            title={tab === 'upcoming' ? 'No upcoming bookings' : 'Nothing here yet'}
            description={
              tab === 'upcoming'
                ? 'Browse services and book a creator between lectures.'
                : undefined
            }
            action={
              tab === 'upcoming' ? (
                <Link to="/discover" className="btn-primary">
                  Browse services
                </Link>
              ) : undefined
            }
          />
        )}
        {visible.map((b) => {
          const when = formatBookingWhen(b.scheduledStart?.toDate?.());
          const next = bookingNextAction(b, side);
          const withWho =
            side === 'buyer' ? b.serviceSnapshot.sellerDisplayName : null;
          return (
            <Link
              key={b.id}
              to={`/bookings/${b.id}`}
              className="group block rounded-lg border border-makhe-line bg-makhe-paper p-4 shadow-soft transition-shadow hover:shadow-lift"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusChip
                      label={BOOKING_STATUS_LABEL[b.status]}
                      tone={BOOKING_STATUS_TONE[b.status]}
                    />
                    {b.urgent && <StatusChip label="Urgent" tone="accent" />}
                    {next && (
                      <span className="text-[11px] uppercase tracking-widest text-makhe-primary">
                        {next}
                      </span>
                    )}
                  </div>
                  <h2
                    className="mt-2 truncate font-display text-xl leading-tight"
                    style={{ fontVariationSettings: '"opsz" 36, "wght" 450' }}
                  >
                    {b.serviceSnapshot.title}
                  </h2>
                  <p className="mt-1 text-sm text-makhe-muted">
                    {when}
                    {withWho ? ` · ${withWho}` : ''}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-mono text-sm text-makhe-primary">
                    {formatZAR(b.pricing.totalZARcents)}
                  </p>
                  <ArrowRight
                    size={16}
                    className="ml-auto mt-3 text-makhe-muted transition-transform group-hover:translate-x-0.5 group-hover:text-makhe-ink"
                  />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
